// ─────────────────────────────────────────────────────────────────────────────
// Crew — tiny no-dependency hash sub-router (Agent B).
//
// main.tsx (frozen) reserves #/metrics and #/privacy and renders them BEFORE
// <App/> mounts. This router lives entirely inside <App/> and owns every other
// hash. It is defensive: if it ever sees #/metrics or #/privacy it treats the
// route as "not ours" (returns null) so we never collide with Agent D.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';

// The app's own views. The empty string / '/' is the home/entry view; the rest
// are deep-linkable sub-views of the signed-in experience.
export type Route = 'home' | 'group' | 'activities' | 'settings';

// Hashes Agent D owns — we never claim these.
const RESERVED = new Set(['/metrics', '/privacy']);

const ROUTE_BY_PATH: Record<string, Route> = {
  '': 'home',
  '/': 'home',
  '/group': 'group',
  '/activities': 'activities',
  '/settings': 'settings',
};

const PATH_BY_ROUTE: Record<Route, string> = {
  home: '/',
  group: '/group',
  activities: '/activities',
  settings: '/settings',
};

function pathFromHash(hash: string): string {
  return hash.replace(/^#/, '');
}

/** Parse the current location hash into one of our routes, or null if reserved. */
export function currentRoute(): Route | null {
  const path = pathFromHash(window.location.hash);
  if (RESERVED.has(path)) return null;
  return ROUTE_BY_PATH[path] ?? 'home';
}

/** Imperatively navigate to one of our views (updates the hash). */
export function navigate(route: Route): void {
  const next = `#${PATH_BY_ROUTE[route]}`;
  if (window.location.hash !== next) window.location.hash = next;
}

/** Subscribe to hash changes and return the active route (null when reserved). */
export function useHashRoute(): Route | null {
  const [route, setRoute] = useState<Route | null>(() => currentRoute());
  useEffect(() => {
    const onChange = () => setRoute(currentRoute());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}
